import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import './CreateQuotesModal.css';
import './CreateQuotesForm.css';
import FormModal from 'components/shared/modal/FormModal';
import {
  continueCreateQuotes,
  setCreateQuotesModalState,
  setShowCreateQuotesModal,
  submitQuotes,
} from 'actions/admin/work-orders/details/create-quote';
import {
  createQuotesFormSelector,
  createQuotesModalStateSelector,
  showCreateQuotesModalSelector,
} from 'selectors/admin/work-orders/details/create-quote';
import CreateQuotesForm from 'components/admin/work-orders/details/quotes/CreateQuotesForm';
import {
  getQuotes,
  getSelectedTasks,
} from 'selectors/admin/work-orders/details/details';
import moment from 'moment';
import numeral from 'numeral';
import Button from 'components/shared/button/Button';
import checkCirclePng from 'images/check-circle.png';
import ConfirmationPanel from 'components/shared/confirmation/ConfirmationPanel';
import { getCurrentWorkOrder } from 'selectors/admin/work-orders/work-orders';
import { deselectAllTasks } from 'actions/admin/work-orders/details/details';
import { getCurrentUser } from 'selectors/authentication';
import { QuotesStatusKeys } from 'domain/quotes-status-enum';

class CreateQuotesModal extends Component {
  static propTypes = {
    createQuotesForm: PropTypes.shape({}),
    createQuotesModalState: PropTypes.string.isRequired,
    currentUser: PropTypes.shape({}).isRequired,
    currentWorkOrder: PropTypes.shape({}).isRequired,
    quotes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    showCreateQuotesModal: PropTypes.bool.isRequired,
    onContinue: PropTypes.func.isRequired,
    onDeselectAllTasks: PropTypes.func.isRequired,
    onQuotesSubmit: PropTypes.func.isRequired,
    onSetCreateQuotesModalState: PropTypes.func.isRequired,
    onShowCreateQuotesModal: PropTypes.func.isRequired,
  };

  static defaultProps = {
    createQuotesForm: null,
  };

  handleContinue = (values) => {
    this.props.onContinue(values);
  };

  handleEnd = () => {
    this.props.onDeselectAllTasks();
    this.props.onShowCreateQuotesModal(false);
  };

  handleSubmit = () => {
    const {
      createQuotesForm,
      currentUser,
      currentWorkOrder,
      quotes,
    } = this.props;
    const values = createQuotesForm.fields.map((field) => {
      const quote = quotes.find((quote) => quote.task.id === field.taskId);
      return {
        quoteId: quote.id,
        startDate: field.startDate,
        durationInDays: field.durationInDays,
        totalCost: numeral(field.totalCost).value(),
        materialLeadDays: field.materialLeadDays,
        description: field.description,
      };
    });

    this.props.onQuotesSubmit(
      currentWorkOrder.id,
      currentUser.accountable_id,
      values
    );
  };

  render() {
    const {
      createQuotesModalState,
      showCreateQuotesModal,
      onShowCreateQuotesModal,
    } = this.props;

    if (showCreateQuotesModal) {
      return (
        <FormModal
          className="create-quotes-modal"
          show={showCreateQuotesModal}
          showHeader={createQuotesModalState !== 'confirmation'}
          title={
            createQuotesModalState === 'edit' ? 'Create Quotes' : 'Edit Quotes'
          }
          onClose={() => onShowCreateQuotesModal(false)}
        >
          {createQuotesModalState === 'edit' && this.renderForm()}
          {createQuotesModalState === 'review' && this.renderReviewForm()}
          {createQuotesModalState === 'confirmation' &&
            this.renderConfirmation()}
        </FormModal>
      );
    }
    return null;
  }

  renderConfirmation() {
    return (
      <ConfirmationPanel
        image={checkCirclePng}
        firstLine="Success!"
        lines={['Quotes have been submitted.']}
        buttons={[
          <Button
            className="button-orange"
            label="Continue"
            onClick={this.handleEnd}
          />,
        ]}
      />
    );
  }

  renderForm() {
    const { createQuotesForm, quotes, selectedTasks } = this.props;
    let initialValues;
    if (createQuotesForm !== null) {
      initialValues = {
        fields: createQuotesForm.fields,
      };
    } else {
      initialValues = {
        fields: Object.keys(selectedTasks)
          .filter((key) => {
            const quote = quotes.find(
              (quote) => quote.task.id === selectedTasks[key].id
            );
            if (quote !== undefined) {
              return (
                quote.status === QuotesStatusKeys.WAITING ||
                quote.status === QuotesStatusKeys.WITHDRAWN
              );
            }
            return false;
          })
          .map((key) => ({
            taskId: numeral(key).value(),
            taskNumber: selectedTasks[key].number,
            taskType: selectedTasks[key].trade.label,
            taskCompletionDate: selectedTasks[key].completion_date,
            startDate: '',
            durationInDays: null,
            totalCost: '',
            materialLeadDays: '',
            description: '',
          })),
      };
    }
    return (
      <CreateQuotesForm
        initialValues={initialValues}
        onSubmit={this.handleContinue}
      />
    );
  }

  renderReviewForm() {
    return (
      <div className="create-quotes-form">
        <table className="create-quotes-form-table">
          <colgroup>
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>TASK</th>
              <th>TYPE</th>
              <th>TARGET COMPLETION DATE</th>
              <th>START DATE</th>
              <th>DURATION IN DAYS</th>
              <th>MATERIAL LEAD TIME IN DAYS</th>
              <th>TOTAL COST</th>
              <th>DESCRIPTION</th>
            </tr>
          </thead>
          <tbody>
            {this.props.createQuotesForm.fields.map((field, index) => (
              <tr key={index}>
                <td>{field.taskNumber}</td>
                <td>{field.taskType}</td>
                <td className="completion-date-readonly">
                  {moment(field.taskCompletionDate).format('M/D/YYYY')}
                </td>
                <td>{moment(field.startDate).format('M/D/YYYY')}</td>
                <td>{field.durationInDays}</td>
                <td>{field.materialLeadDays}</td>
                <td className="total-cost-readonly">
                  {numeral(field.totalCost).format('$0,0')}
                </td>
                <td>{field.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="buttons-container">
          <Button
            className="button-orange"
            label="Submit Quotes"
            onClick={this.handleSubmit}
          />
          <Button
            className="button-gray"
            label="Edit Quotes"
            onClick={() => this.props.onSetCreateQuotesModalState('edit')}
          />
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    createQuotesForm: createQuotesFormSelector(state),
    createQuotesModalState: createQuotesModalStateSelector(state),
    currentUser: getCurrentUser(state),
    currentWorkOrder: getCurrentWorkOrder(state),
    quotes: getQuotes(state),
    selectedTasks: getSelectedTasks(state),
    showCreateQuotesModal: showCreateQuotesModalSelector(state),
  };
}

const mapDispatchToProps = (dispatch) => ({
  onContinue: (values) => {
    dispatch(continueCreateQuotes(values));
  },
  onDeselectAllTasks: () => {
    dispatch(deselectAllTasks());
  },
  onQuotesSubmit: (workOrderId, contractorId, values) => {
    dispatch(submitQuotes(workOrderId, contractorId, values));
  },
  onSetCreateQuotesModalState: (value) => {
    dispatch(setCreateQuotesModalState(value));
  },
  onShowCreateQuotesModal: (value) => {
    dispatch(setShowCreateQuotesModal(value));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateQuotesModal);



// WEBPACK FOOTER //
// ./src/components/admin/work-orders/details/quotes/CreateQuotesModal.js